// Web3Forms Service - Envoi de formulaires par email sans CORS
// Documentation: https://web3forms.com/

const WEB3FORMS_ACCESS_KEY = 'd6e5c8ac-dbc7-4aad-be3c-542940e80eb4'

interface Web3FormsData {
  type: string
  fields: Record<string, string>
}

function getFormTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    atelier: '📋 Inscription Atelier Mars 2026',
    ateliers: '📋 Inscription Atelier Mars 2026',
    festival: '🎉 Participation Festival Juin 2026',
    employeur: '🏢 Demande Employeur Partenaire',
    recruter: '👤 Demande de Recrutement',
    partenariat: '🤝 Demande de Partenariat',
    recyclage: '♻️ Demande de Recyclage',
    membre: '💙 Demande Adhésion Membre',
    formateur: '🎓 Candidature Formateur',
    benevole: '🤲 Candidature Bénévole',
    newsletter: '📧 Inscription Newsletter',
    formation: '📚 Inscription Formation ADCC',
    don: '💝 Demande de Don ADCC',
    promaison_employeur: '🏠 PRO MAISON — Demande Employeur / Résidence',
    promaison_professionnel: '🧹 PRO MAISON — Inscription Professionnel(le)',
  }
  return labels[type] || type
}

function formatFieldLabel(key: string): string {
  const labels: Record<string, string> = {
    nom: 'Nom',
    prenom: 'Prénom',
    email: 'Email',
    telephone: 'Téléphone',
    age: 'Âge',
    whatsapp: 'WhatsApp',
    typeFormation: 'Type de formation',
    charteEngagement: "Approbation charte d'engagement",
    dateNaissance: 'Date de naissance',
    sexe: 'Sexe',
    situation: 'Situation professionnelle',
    niveauEtudes: "Niveau d'études",
    ville: 'Ville',
    quartier: 'Quartier',
    formationSouhaitee: 'Formation souhaitée',
    formationAutre: 'Autre formation (précision)',
    source: "Comment avez-vous connu l'ADCC",
    motivations: 'Motivations',
    profil: 'Profil',
    participation: 'Participation souhaitée',
    message: 'Message',
    typeEmployeur: "Type d'employeur",
    profilsRecherches: 'Profils recherchés',
    attentes: 'Attentes',
    typePersonnel: 'Type de personnel recherché',
    typeContrat: 'Type de contrat',
    dateDebut: 'Date de début souhaitée',
    description: 'Description du poste',
    organisation: 'Organisation',
    typeOrganisation: "Type d'organisation",
    nomContact: 'Nom du contact',
    fonction: 'Fonction',
    typePartenariat: 'Type de partenariat',
    proposition: 'Proposition',
    nomEmployeur: "Nom de l'employeur",
    telephoneEmployeur: "Téléphone de l'employeur",
    emailEmployeur: "Email de l'employeur",
    villeEmployeur: "Ville de l'employeur",
    nomEmploye: "Nom de l'employé",
    posteActuel: 'Poste actuel',
    anciennete: 'Ancienneté',
    telephoneEmploye: "Téléphone de l'employé",
    pointsFormation: 'Points de formation',
    autresPoints: 'Autres points de formation',
    periodeSouhaitee: 'Période souhaitée',
    dureeSouhaitee: 'Durée souhaitée',
    observations: 'Observations',
    typeMembre: 'Type de membre',
    profession: 'Profession',
    contribution: 'Contribution souhaitée',
    domaine: "Domaine d'expertise",
    experience: 'Expérience',
    disponibilite: 'Disponibilité',
    competences: 'Compétences',
    motivation: 'Motivation',
    metier: 'Métier souhaité',
    charteAcceptee: 'Charte éthique acceptée',
    cv: 'CV',
    partnerType: 'Type de partenaire',
    orgName: 'Organisation',
    responsibleName: 'Nom du contact',
    phone: 'Téléphone',
    address: 'Adresse',
    needs: 'Besoins en recrutement',
    nomComplet: 'Nom complet',
    interet: "Centre d'intérêt",
    metiers: 'Métiers qui intéressent',
    commentaire: 'Commentaire',
    consentement: 'Consentement données (loi 2017-20)',
  }
  return labels[key] || key.charAt(0).toUpperCase() + key.slice(1)
}

export async function sendViaWeb3Forms(
  data: Web3FormsData,
): Promise<{ success: boolean; message: string }> {
  console.log('📧 Web3Forms submission:', data.type)

  const accessKey = WEB3FORMS_ACCESS_KEY
  if (!accessKey || accessKey === 'YOUR_WEB3FORMS_ACCESS_KEY_HERE') {
    console.warn('⚠️ Web3Forms access key not configured')
    return {
      success: true,
      message: '🎉 Inscription enregistrée localement. (Email non configuré)',
    }
  }

  try {
    const typeLabel = getFormTypeLabel(data.type)

    // Build the payload with ALL fields as separate top-level keys
    // Web3Forms displays each key as its own field in the email
    const payload: Record<string, string> = {
      access_key: accessKey,
      subject: `${typeLabel} — ${data.fields.prenom || ''} ${data.fields.nom || data.fields.nomEmployeur || data.fields.nomContact || data.fields.responsibleName || ''} | ADCC`,
      from_name: 'ADCC Site Web',
      'Type de formulaire': typeLabel,
    }

    // Add each form field as a separate labeled key
    Object.entries(data.fields).forEach(([key, value]) => {
      // Skip empty values and file inputs
      if (!value || value.trim() === '' || key === 'cv') return

      const label = formatFieldLabel(key)
      payload[label] = value
    })

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })

    const result = await response.json()
    console.log('📧 Web3Forms response:', result)

    if (result.success) {
      console.log('✅ Email envoyé via Web3Forms')
      return {
        success: true,
        message:
          '🎉 Inscription envoyée avec succès ! Vous recevrez une confirmation bientôt.',
      }
    } else {
      console.error('❌ Web3Forms error:', result)
      return {
        success: false,
        message: "Erreur lors de l'envoi. Veuillez réessayer.",
      }
    }
  } catch (error) {
    console.error('❌ Web3Forms network error:', error)
    return {
      success: false,
      message: 'Erreur réseau. Veuillez réessayer.',
    }
  }
}
